// Frame-by-frame re-analysis of a recorded clip. The live loop analyses as many
// frames as the phone keeps up with; here every frame can be analysed, with any
// model variant, which is what the validation lab uses to compare models.

import { analyzeAttempt } from '../core/analysis';
import { paramsFor, type AnalysisParams } from '../core/params';
import { deserializeFrames, serializeFrames, type PoseFrame } from '../core/pose';
import { db, type AttemptRecord, type CalibrationRecord, type ModelVariant, type Settings } from './db';
import { createDetector } from './pose';

export interface ClipAnalysis {
  frames: PoseFrame[];
  width: number;
  height: number;
}

async function loadVideo(blob: Blob): Promise<{ video: HTMLVideoElement; url: string; duration: number }> {
  const url = URL.createObjectURL(blob);
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.src = url;
  video.load(); // iOS may not fetch data for a video that isn't on the page otherwise
  await new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error('The clip didn’t load.')), 15000);
    video.onloadeddata = () => {
      clearTimeout(timeout);
      resolve();
    };
    video.onerror = () => {
      clearTimeout(timeout);
      reject(new Error('This clip can’t be decoded by the browser.'));
    };
  });
  let duration = video.duration;
  if (!Number.isFinite(duration)) {
    // MediaRecorder WebM files have no duration until the end has been seen.
    video.currentTime = 1e7;
    await new Promise<void>((r) => (video.onseeked = () => r()));
    duration = video.duration;
    video.currentTime = 0;
    await new Promise<void>((r) => (video.onseeked = () => r()));
  }
  return { video, url, duration };
}

async function seek(video: HTMLVideoElement, t: number) {
  await new Promise<void>((resolve) => {
    video.onseeked = () => resolve();
    video.currentTime = t;
  });
  // Give the decoder a moment to present the frame.
  await new Promise<void>((resolve) => {
    const done = setTimeout(resolve, 80);
    if ('requestVideoFrameCallback' in video) video.requestVideoFrameCallback(() => (clearTimeout(done), resolve()));
  });
}

export async function analyseClip(
  blob: Blob,
  variant: ModelVariant,
  delegate: 'GPU' | 'CPU',
  opts: { fps?: number; onProgress?: (fraction: number) => void; signal?: AbortSignal; maxMs?: number } = {},
): Promise<ClipAnalysis> {
  const { video, url, duration: rawDuration } = await loadVideo(blob);
  // Never trust an unknown duration: fall back to the attempt's own length.
  const duration = Number.isFinite(rawDuration) && rawDuration > 0 ? Math.min(rawDuration, (opts.maxMs ?? 60000) / 1000) : (opts.maxMs ?? 10000) / 1000;
  const detector = await createDetector(variant, delegate);
  try {
    const step = 1000 / (opts.fps ?? 30);
    const frames: PoseFrame[] = [];
    const end = duration * 1000;
    for (let t = 0; t <= end; t += step) {
      if (opts.signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
      await seek(video, t / 1000);
      frames.push({ t, lm: detector.detect(video, t) });
      opts.onProgress?.(Math.min(1, t / end));
    }
    return { frames, width: video.videoWidth, height: video.videoHeight };
  } finally {
    detector.close();
    URL.revokeObjectURL(url);
    video.removeAttribute('src');
  }
}

export function paramsForSettings(settings: Settings, calibration: CalibrationRecord): AnalysisParams {
  return paramsFor({ strictness: settings.strictness, holdMs: settings.holdMs, checkAngle: settings.checkAngle }, calibration.reference.noise);
}

/** Re-scores stored frames with the current settings and algorithm. */
export function rescore(a: AttemptRecord, calibration: CalibrationRecord, params: AnalysisParams): AttemptRecord {
  const analysis = analyzeAttempt(deserializeFrames(a.frames), {
    mapping: calibration.mapping,
    reference: calibration.reference,
    params,
    stance: calibration.stance,
    cueTime: a.cueTime ?? undefined,
  });
  return { ...a, analysis };
}

export async function rescoreAll(settings: Settings, onProgress?: (done: number, total: number) => void): Promise<number> {
  const attempts = await db.allAttempts();
  const calibrations = new Map((await db.listCalibrations()).map((c) => [c.id, c]));
  let n = 0;
  for (const a of attempts) {
    const cal = calibrations.get(a.calibrationId);
    if (!cal) continue;
    await db.putAttempt(rescore(a, cal, paramsForSettings(settings, cal)));
    onProgress?.(++n, attempts.length);
  }
  return n;
}

/** Re-runs pose detection on an attempt's clip with another model; stored alongside the original. */
export async function reanalyseAttempt(
  a: AttemptRecord,
  calibration: CalibrationRecord,
  settings: Settings,
  variant: ModelVariant,
  onProgress?: (f: number) => void,
): Promise<AttemptRecord> {
  if (!a.videoKey) throw new Error('This attempt has no video clip.');
  const blob = await db.getBlob(a.videoKey);
  if (!blob) throw new Error('The clip is missing.');
  const lastT = deserializeFrames(a.frames).at(-1)?.t ?? 10000;
  const clip = await analyseClip(blob, variant, settings.delegate, { onProgress, maxMs: lastT - a.videoOffsetMs + 500 });
  const shifted = clip.frames.map((f) => ({ t: f.t + a.videoOffsetMs, lm: f.lm }));
  const params = paramsForSettings(settings, calibration);
  const analysis = analyzeAttempt(shifted, {
    mapping: calibration.mapping,
    reference: calibration.reference,
    params,
    stance: calibration.stance,
    cueTime: a.cueTime ?? undefined,
  });
  const updated: AttemptRecord = {
    ...a,
    alt: { ...(a.alt ?? {}), [variant]: { frames: serializeFrames(shifted), analysis, createdAt: Date.now() } },
  };
  await db.putAttempt(updated);
  return updated;
}
